import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Sparkles, X, Send, Loader2, AlertTriangle, Database, Copy, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  'Show students whose medical expires in the next 30 days',
  'Which aircraft are currently grounded?',
  'Students with program completion over 80%',
  'List instructors with expiring licenses this quarter',
  'Flights longer than 3 hours in the last 30 days',
];

function ResultsTable({ rows, fields }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-muted-foreground py-4 text-center">No matching rows.</p>;
  }
  // Show only fields that actually have a value in at least one row
  const shown = fields.filter((f) => rows.some((r) => r[f] !== undefined && r[f] !== null && r[f] !== ''));
  return (
    <div className="overflow-auto rounded-lg border border-white/10 max-h-[40vh]">
      <table className="w-full text-xs">
        <thead className="bg-slate-900/80 sticky top-0">
          <tr>
            {shown.map((f) => (
              <th key={f} className="text-left p-2 text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{f.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.student_id || r.log_id || r.aircraft_id || `row-${i}`} className="border-t border-white/5 hover:bg-white/[0.02]">
              {shown.map((f) => {
                const val = r[f];
                let display;
                if (val === null || val === undefined) display = '-';
                else if (typeof val === 'object') display = JSON.stringify(val).slice(0, 80);
                else if (typeof val === 'boolean') display = val ? 'yes' : 'no';
                else display = String(val).slice(0, 80);
                return <td key={f} className="p-2 text-slate-300 align-top">{display}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function NLQueryBar() {
  const { user } = useAuth();
  const canUse = user?.role === 'admin' || user?.role === 'auditor';
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!canUse) return undefined;
    // Open with Cmd/Ctrl+J
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canUse, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setError(null);
    }
  }, [open]);

  const submit = useCallback(async () => {
    const q = question.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(`${API}/nl-query/ask`, { question: q, limit: 50 }, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Query failed');
    } finally {
      setLoading(false);
    }
  }, [question, loading]);

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } };

  const copyQuery = async () => {
    if (!result?.query_ran) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result.query_ran, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (!canUse) return null;

  return (
    <>
      <button
        data-testid="nl-query-fab"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-violet-500/30 bg-gradient-to-r from-violet-500/15 to-indigo-500/15 hover:from-violet-500/25 hover:to-indigo-500/25 text-violet-200 text-xs font-semibold transition-all"
        title="Ask your data (Cmd/Ctrl+J)"
      >
        <Sparkles className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Ask your data</span>
        <kbd className="hidden lg:inline-flex text-[9px] px-1.5 py-0.5 rounded bg-black/30 border border-white/10 font-mono">⌘J</kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9998] flex items-start justify-center pt-[10vh] px-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="nl-query-overlay"
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-violet-500/25 overflow-hidden shadow-2xl shadow-black/50"
            style={{ background: 'hsl(222 47% 9% / 0.98)', backdropFilter: 'blur(24px)' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="nl-query-modal"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
              <div className="w-8 h-8 rounded-lg bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-violet-300" />
              </div>
              <h3 className="text-sm font-semibold text-foreground flex-1">Ask your data</h3>
              <span className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">Claude Sonnet 4.5</span>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/5 text-slate-400" data-testid="nl-query-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="nl-query-input"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="e.g. Show students whose medical expires in the next 30 days"
                  rows={2}
                  className="flex-1 px-3 py-2.5 rounded-lg bg-slate-900 border border-white/10 text-sm text-foreground focus:outline-none focus:border-violet-500/50 resize-none"
                />
                <button
                  onClick={submit}
                  disabled={loading || !question.trim()}
                  data-testid="nl-query-submit"
                  className="px-4 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {loading ? 'Thinking' : 'Ask'}
                </button>
              </div>

              {!result && !loading && !error && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-mono">Try asking</p>
                  <div className="flex flex-col gap-1.5">
                    {EXAMPLES.map((ex, i) => (
                      <button
                        key={ex}
                        data-testid={`nl-query-example-${i}`}
                        onClick={() => setQuestion(ex)}
                        className="text-left text-xs text-slate-400 hover:text-violet-300 py-1.5 px-2 rounded hover:bg-violet-500/5 transition-colors"
                      >
                        &ldquo;{ex}&rdquo;
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-start gap-2 text-xs text-rose-300" data-testid="nl-query-error">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>{error}</div>
                </div>
              )}

              {result && result.ok === false && (
                <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300" data-testid="nl-query-out-of-scope">
                  <p className="font-semibold mb-1">Can&apos;t answer this one</p>
                  <p className="text-amber-300/80">{result.explanation}</p>
                </div>
              )}

              {result && result.ok && (
                <div className="mt-4 space-y-3" data-testid="nl-query-result">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-[11px] text-slate-400">
                      <Database className="w-3 h-3" />
                      <span className="font-mono text-violet-300">{result.collection}</span>
                      <span>·</span>
                      <span>{result.row_count} row{result.row_count !== 1 ? 's' : ''}</span>
                    </div>
                    <button
                      onClick={copyQuery}
                      data-testid="nl-query-copy"
                      className="text-[10px] text-slate-400 hover:text-violet-300 flex items-center gap-1 font-mono"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied' : 'Copy Mongo query'}
                    </button>
                  </div>
                  {result.explanation && (
                    <p className="text-xs text-slate-300 italic">&ldquo;{result.explanation}&rdquo;</p>
                  )}
                  <ResultsTable rows={result.rows} fields={result.fields} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
